/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, MouseEventHandler, useContext, useEffect, useImperativeHandle, useState} from 'react';
import {CompleteInvoiceFilter, CustomerType, OrderStatus} from "../../../util/interfaces";
import {getFromLocal} from "../../../util/storageUtil";
import InvoiceFilter from "./invoiceFilter";
import {AppContext} from "../../misc/appContext";

const LOCAL_STORAGE_ORDER_STATUS = 'bolmate-order-status-filter';
const emptyFilters: CompleteInvoiceFilter = {
    status: '', order_status: (getFromLocal(LOCAL_STORAGE_ORDER_STATUS) as OrderStatus | null) ?? '',
    order_id: null, reference: null, coc_number: null, company: null, country: null, date_from: null, date_to: null,
    invoice_type: null, firstname: null, lastname: null, vat_number: null,
    customer_type: CustomerType.BOTH, invoice_num: null, ean: null,
};

export type InvoiceFilterRef = {
    onReset: VoidFunction;
}

interface Props {
    onSubmit: (filters: CompleteInvoiceFilter) => void;
    onClose: MouseEventHandler & ((evt: MouseEvent) => void);
    active: boolean;
    filter: Partial<CompleteInvoiceFilter>
}

function InvoiceFilterController(props: Props, ref: ForwardedRef<InvoiceFilterRef>) {
    const context = useContext(AppContext);
    const {onSubmit, active, onClose, filter} = props;
    const [filters, setFilters] = useState(Object.assign({}, emptyFilters, filter));

    function onChangeFilter(key: keyof CompleteInvoiceFilter) {
        return function (value: string | null) {
            setFilters(Object.assign({}, filters, {[key]: value}));
        }
    }

    function onResetFilters() {
        setFilters(emptyFilters);
        onSubmit(emptyFilters);
    }

    function onSubmitFilters() {
        onSubmit(filters);
    }

    useImperativeHandle(ref, () => ({
        onReset: onResetFilters,
    }));

    useEffect(() => {
        setFilters(Object.assign({}, filters, filter));
    }, [filter]);

    useEffect(() => {
        setFilters(emptyFilters);
    }, [context.accountContext.account]);

    return (
        <InvoiceFilter onChangeFilter={onChangeFilter} onSubmitFilters={onSubmitFilters} onResetFilters={onResetFilters}
                       onClose={onClose} filters={filters} active={active}/>
    );
}

export default forwardRef<InvoiceFilterRef, Props>(InvoiceFilterController);